import { usePathname } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import type { OrderSummaryData } from 'lib/components/OrderSummary/OrderSummary'
import OrderSummary from 'lib/components/OrderSummary/OrderSummary'
import { PromoCode } from 'lib/components/PaymentDetails/partials/PromoCodeInput'

import { routes } from '@/app/book/routes'
import useVirtualCartStore from '@/app/book/stores/useVirtualCartStore'
import StickyOnScroll from '@/components/StickyOnScroll'
import { reformatOrderSummary } from '@/services/orderService'
import useSessionStore from '@/stores/useSessionStore'
import { brand } from '@/utils/brand'
import { recordAction } from '@/utils/rum'

import { useCart } from '../hooks/useCart'
import { steps } from '../steps'
import useCartStore from '../stores/useCartStore'
import useLayoutPropsStore from '../stores/useLayoutPropsStore'
import useStepsStore from '../stores/useStepsStore'
import { resolveInternational } from '../utils/orderSummary'

export interface SidebarProps {
  cartClearOnClick?: () => void
  ctaDisabled?: boolean
  ctaLabel?: string
  ctaOnClick?: (data?: OrderSummaryData) => void
  mobileCtaLabel?: string
  showLoadingIcon?: boolean
}

const Sidebar: React.FC<SidebarProps> = ({
  cartClearOnClick,
  ctaDisabled = false,
  ctaLabel,
  ctaOnClick,
  mobileCtaLabel,
  showLoadingIcon = false,
}: SidebarProps) => {
  const pathName = usePathname()
  const currentStep = routes.find(({ path }) => path === pathName)?.stepNumber || 0
  const { steps: stepsFromStore } = useStepsStore((state) => state)
  const completedSteps = stepsFromStore
    .filter(({ completed }) => completed)
    .map(({ number }) => number)

  const { cart, promoCodes, removePromo, isPending } = useCart()
  const userRoles = useSessionStore((state) => state.userRoles)
  const termsAccepted = useSessionStore((state) => state.termsAccepted)
  const needsTermsAcceptance = !termsAccepted

  const { terms, termsError, setTerms, setTermsError } = useLayoutPropsStore()

  const cartOrder = reformatOrderSummary(cart?.orderSummary)
  const [isInternational, setInternational] = useState<boolean>(false)

  const [noReceiptCheckboxVisible, setNoReceiptCheckboxVisible] = useState<boolean>(false)
  const noReceiptChecked = useCartStore((state) => state.noReceipt)
  const setNoReceipt = useCartStore((state) => state.setNoReceipt)

  const virtualCart = useVirtualCartStore((state) => state.data)

  useEffect(() => {
    setNoReceiptCheckboxVisible(userRoles?.includes('sales_manager'))
  }, [userRoles])

  const onCTAClick = useCallback(
    async (data?: OrderSummaryData) => {
      ctaOnClick?.(data)
    },
    [ctaOnClick],
  )

  const onRemovePromoCode = (promoCode: PromoCode): void => {
    removePromo(promoCode)
  }

  const onQuoteSaveClick = (): void => {}

  const onNoReceiptChange = useCallback(
    (noReceipt: boolean) => {
      setNoReceipt(noReceipt)
    },
    [setNoReceipt],
  )

  useEffect(() => {
    if (!cart?.orderId || !cart?.id) return

    recordAction('activeCart', {
      orderId: cart?.orderId,
      cartId: cart?.id,
    })
  }, [cart?.orderId, cart?.id])

  useEffect(() => {
    if (cart) setInternational(resolveInternational(cart))
  }, [cart])

  const className = useMemo(() => {
    if (isPending) {
      return 'opacity-50'
    }
    return ''
  }, [isPending])

  // Build out order data, adding static data on unavailable
  return (
    <StickyOnScroll
      className="lg:top-[100px]"
      render={() => (
        <OrderSummary
          brand={brand.key}
          cartId={cart?.displayId}
          className={className}
          completed={completedSteps}
          convertPrice={(amount) => amount}
          ctaDisabled={ctaDisabled}
          ctaLabel={ctaLabel}
          current={currentStep}
          isInternational={isInternational}
          isGuestUser={needsTermsAcceptance}
          mobileCtaLabel={mobileCtaLabel}
          showNoReceiptCheckbox={noReceiptCheckboxVisible}
          onCartClearClick={cartClearOnClick}
          onCTAClick={ctaOnClick ? onCTAClick : undefined}
          onQuoteSaveClick={onQuoteSaveClick}
          onRemovePromoCode={onRemovePromoCode}
          order={currentStep === 1 ? virtualCart || undefined : cartOrder}
          pathName={pathName}
          promoCodes={promoCodes}
          quoteSaved={false}
          showLoadingIcon={isPending || showLoadingIcon}
          smsChecked={false}
          steps={steps.length}
          terms={terms}
          termsError={termsError}
          onTermsChange={setTerms}
          onTermsErrorChange={setTermsError}
          onNoReceiptChange={onNoReceiptChange}
          noReceiptChecked={noReceiptChecked}
        />
      )}
    />
  )
}

export default React.memo(Sidebar)
